/**
 * Story tab: lifecycle cards with kind-colored accent and badges.
 */

import { ChevronRight } from 'lucide-react';

import type { DropTimelineItem } from '@/hooks';
import { cn } from '@/lib/utils';

import { formatDropTimelineAt } from './drop-timeline-format';

const KIND_LABEL: Record<string, string> = {
  created: 'Created',
  updated: 'Updated',
  sent: 'Sent',
  killed: 'Killed',
  failed: 'Failed',
  other: 'Activity',
};

function kindAccentClass(kind: string): string {
  switch (kind) {
    case 'created':
      return 'bg-zinc-400 dark:bg-zinc-500';
    case 'sent':
      return 'bg-emerald-500 dark:bg-emerald-400';
    case 'killed':
      return 'bg-red-500 dark:bg-red-400';
    case 'failed':
      return 'bg-destructive';
    case 'updated':
      return 'bg-sky-500 dark:bg-sky-400';
    default:
      return 'bg-muted-foreground/40';
  }
}

function kindBadgeClass(kind: string): string {
  switch (kind) {
    case 'created':
      return 'border-zinc-200 bg-zinc-100 text-zinc-800 dark:border-zinc-600 dark:bg-zinc-900/50 dark:text-zinc-200';
    case 'sent':
      return 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200';
    case 'killed':
      return 'border-red-200 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200';
    case 'failed':
      return 'border-destructive/40 bg-destructive/10 text-destructive';
    case 'updated':
      return 'border-sky-200 bg-sky-50 text-sky-900 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-200';
    default:
      return 'border-border bg-muted/60 text-muted-foreground';
  }
}

function detailHint(source: DropTimelineItem['source']): string {
  return source === 'siem' ? 'View SIEM log' : 'View audit record';
}

export function TimelineStoryPanel(props: {
  items: DropTimelineItem[];
  windowLabel: string;
  onSelectItem?: (item: DropTimelineItem) => void;
}) {
  if (props.items.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-6">
        No SIEM story events in this window. Use &quot;All activity&quot; for audit + full SIEM.
      </p>
    );
  }

  const interactive = props.onSelectItem != null;

  return (
    <div className="space-y-1">
      <p className="text-xs text-muted-foreground mb-3">{props.windowLabel}</p>
      <ul className="space-y-3">
        {props.items.map((item) => {
          const body = (
            <>
              <div
                className={`w-1 shrink-0 ${kindAccentClass(item.kind)}`}
                aria-hidden
              />
              <div className="min-w-0 flex-1 px-3 py-2.5">
                <div className="flex flex-wrap items-center gap-2 gap-y-1">
                  <span
                    className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold tracking-tight ${kindBadgeClass(item.kind)}`}
                  >
                    {KIND_LABEL[item.kind] ?? item.kind}
                  </span>
                  <time className="text-xs tabular-nums text-muted-foreground">
                    {formatDropTimelineAt(item.occurredAt)}
                  </time>
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/75">
                    {item.source}
                  </span>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  <span className="text-muted-foreground">By </span>
                  <span className="font-medium text-foreground/90 break-all">
                    {item.userEmail ?? '—'}
                  </span>
                </p>
                <p className="mt-1.5 break-all font-mono text-[11px] leading-snug text-muted-foreground">
                  {item.summary}
                </p>
                {interactive ? (
                  <p className="mt-2 flex items-center gap-1 text-xs font-medium text-primary">
                    {detailHint(item.source)}
                    <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  </p>
                ) : null}
              </div>
            </>
          );

          return (
            <li
              key={`${item.source}-${item.id}`}
              className={cn(
                'flex min-h-0 overflow-hidden rounded-lg border border-border/70 bg-card/30 shadow-sm',
                interactive && 'transition-colors hover:border-border',
              )}
            >
              {interactive ? (
                <button
                  type="button"
                  className="flex min-h-0 w-full min-w-0 overflow-hidden text-left outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={() => props.onSelectItem?.(item)}
                >
                  {body}
                </button>
              ) : (
                <div className="flex min-h-0 w-full overflow-hidden">{body}</div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
